import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
  UpdatePaymentConfigInput,
  usePaymentConfig,
  useUpdatePaymentConfig,
} from '@shared/api/hooks/usePaymentConfig';

interface FormValues {
  stripePublishableKey?: string;
  stripeSecretKey?: string;
  stripeWebhookSecret?: string;
  paypalClientId?: string;
  paypalSecret?: string;
  paypalWebhookId?: string;
  paypalEnv?: 'sandbox' | 'live';
}

export function PaymentSettingsPage(): React.ReactElement {
  const [form] = Form.useForm<FormValues>();
  const { data: config, isLoading, refetch, isFetching } = usePaymentConfig();
  const updateMutation = useUpdatePaymentConfig();
  const [secretTouched, setSecretTouched] = useState(false);
  const [webhookTouched, setWebhookTouched] = useState(false);
  const [paypalSecretTouched, setPaypalSecretTouched] = useState(false);

  React.useEffect(() => {
    if (config) {
      form.setFieldsValue({
        stripePublishableKey: config.stripePublishableKey ?? '',
        paypalClientId: config.paypalClientId ?? '',
        paypalWebhookId: config.paypalWebhookId ?? '',
        paypalEnv: (config.paypalEnv as 'sandbox' | 'live') ?? 'sandbox',
      });
    }
  }, [config, form]);

  const onSave = async (): Promise<void> => {
    const v = await form.validateFields();
    // Secret + webhook are write-only: only send them when the operator
    // actually typed something, so an untouched form never wipes them.
    const input: UpdatePaymentConfigInput = {
      stripePublishableKey: v.stripePublishableKey ?? '',
    };
    if (secretTouched) input.stripeSecretKey = v.stripeSecretKey ?? '';
    if (webhookTouched) input.stripeWebhookSecret = v.stripeWebhookSecret ?? '';
    input.paypalClientId = v.paypalClientId ?? '';
    input.paypalWebhookId = v.paypalWebhookId ?? '';
    input.paypalEnv = v.paypalEnv ?? 'sandbox';
    if (paypalSecretTouched) input.paypalSecret = v.paypalSecret ?? '';
    try {
      await updateMutation.mutateAsync(input);
      message.success('Configurazione pagamenti salvata');
      form.setFieldsValue({ stripeSecretKey: '', stripeWebhookSecret: '', paypalSecret: '' });
      setSecretTouched(false);
      setWebhookTouched(false);
      setPaypalSecretTouched(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore salvataggio'));
    }
  };

  const copyWebhook = (): void => {
    if (config?.webhookUrl) {
      void navigator.clipboard.writeText(config.webhookUrl);
      message.success('URL webhook copiato');
    }
  };

  if (isLoading || !config) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          Pagamenti — Stripe
        </Typography.Title>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Ricarica
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={updateMutation.isPending}
          >
            Salva
          </Button>
        </Space>
      }
    >
      {!config.encryptionReady && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Cifratura non disponibile"
          description="ENCRYPTION_KEY non è configurata sul server: non è possibile salvare la chiave segreta o il webhook secret finché non viene impostata."
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Le chiavi sono per club"
        description="La chiave segreta e il webhook secret vengono cifrati prima di essere salvati e non sono più leggibili dal BO — puoi solo sostituirli. Usa chiavi dello stesso account Stripe."
      />

      <Space style={{ marginBottom: 16 }} wrap>
        <Tag color={config.stripeSecretKeyConfigured ? 'green' : 'default'}>
          Chiave segreta: {config.stripeSecretKeyConfigured
            ? `configurata ••••${config.stripeSecretKeyLast4 ?? ''}`
            : 'non configurata'}
        </Tag>
        <Tag color={config.stripePublishableKey ? 'green' : 'default'}>
          Publishable key: {config.stripePublishableKey ? 'configurata' : 'non configurata'}
        </Tag>
        <Tag color={config.stripeWebhookConfigured ? 'green' : 'default'}>
          Webhook: {config.stripeWebhookConfigured ? 'configurato' : 'non configurato'}
        </Tag>
      </Space>

      <Card size="small" type="inner" title="Endpoint webhook (da incollare nella dashboard Stripe)" style={{ marginBottom: 16 }}>
        <Space>
          <Typography.Text code copyable={false} style={{ fontSize: 12 }}>
            {config.webhookUrl}
          </Typography.Text>
          <Button size="small" icon={<CopyOutlined />} onClick={copyWebhook}>
            Copia
          </Button>
        </Space>
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
          Eventi: payment_intent.succeeded, payment_intent.payment_failed, charge.refunded
        </Typography.Paragraph>
      </Card>

      <Form form={form} layout="vertical">
        <Form.Item
          name="stripePublishableKey"
          label="Publishable key (pk_...)"
          tooltip="Chiave pubblica — usata dall'app mobile. Salvata in chiaro."
        >
          <Input placeholder="pk_live_... / pk_test_..." autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="stripeSecretKey"
          label="Secret key (sk_...)"
          tooltip="Chiave segreta — cifrata at-rest, write-only. Lascia vuoto per non modificarla."
        >
          <Input.Password
            placeholder={
              config.stripeSecretKeyConfigured
                ? `Configurata ••••${config.stripeSecretKeyLast4 ?? ''} — inserisci per sostituire`
                : 'sk_live_... / sk_test_...'
            }
            autoComplete="off"
            onChange={() => setSecretTouched(true)}
          />
        </Form.Item>
        <Form.Item
          name="stripeWebhookSecret"
          label="Webhook signing secret (whsec_...)"
          tooltip="Cifrato at-rest, write-only. Lascia vuoto per non modificarlo."
        >
          <Input.Password
            placeholder={
              config.stripeWebhookConfigured
                ? 'Configurato — inserisci per sostituire'
                : 'whsec_...'
            }
            autoComplete="off"
            onChange={() => setWebhookTouched(true)}
          />
        </Form.Item>

        <Card size="small" type="inner" title="PayPal" style={{ marginTop: 24 }}>
          <Space style={{ marginBottom: 12 }} wrap>
            <Tag color={config.paypalClientId ? 'green' : 'default'}>
              Client ID: {config.paypalClientId ? 'configurato' : 'non configurato'}
            </Tag>
            <Tag color={config.paypalSecretConfigured ? 'green' : 'default'}>
              Secret: {config.paypalSecretConfigured ? `••••${config.paypalSecretLast4 ?? ''}` : 'non configurato'}
            </Tag>
            <Tag color={config.paypalEnv === 'live' ? 'red' : 'blue'}>
              Ambiente: {config.paypalEnv}
            </Tag>
          </Space>

          <Card size="small" type="inner" title="Webhook PayPal (da incollare nel PayPal Developer Dashboard)" style={{ marginBottom: 12 }}>
            <Space>
              <Typography.Text code style={{ fontSize: 12 }}>{config.paypalWebhookUrl}</Typography.Text>
              <Button size="small" icon={<CopyOutlined />} onClick={() => { void navigator.clipboard.writeText(config.paypalWebhookUrl); message.success('URL webhook PayPal copiato'); }}>
                Copia
              </Button>
            </Space>
            <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
              Eventi: PAYMENT.CAPTURE.COMPLETED, PAYMENT.CAPTURE.DENIED, PAYMENT.CAPTURE.DECLINED. Copia il Webhook ID generato qui sotto.
            </Typography.Paragraph>
          </Card>

          <Form.Item name="paypalEnv" label="Ambiente">
            <Select
              options={[
                { value: 'sandbox', label: 'Sandbox (test)' },
                { value: 'live', label: 'Live (produzione)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="paypalClientId" label="Client ID">
            <Input placeholder="A21AA..." autoComplete="off" />
          </Form.Item>
          <Form.Item name="paypalSecret" label="Secret" tooltip="Cifrato at-rest, write-only. Lascia vuoto per non modificarlo.">
            <Input.Password
              placeholder={config.paypalSecretConfigured ? `Configurato ••••${config.paypalSecretLast4 ?? ''} — inserisci per sostituire` : 'EC...'}
              autoComplete="off"
              onChange={() => setPaypalSecretTouched(true)}
            />
          </Form.Item>
          <Form.Item name="paypalWebhookId" label="Webhook ID" tooltip="ID del webhook creato nel PayPal Developer Dashboard.">
            <Input placeholder="WH-..." autoComplete="off" />
          </Form.Item>
        </Card>
      </Form>
    </Card>
  );
}
